import { useMemo, useState } from 'react';
import { Square, SquareCheck, Trash2, UserPlus } from 'lucide-react';
import { useTickets } from '../contexts/TicketsProvider';
import UserAvatar from './UserAvatar';
import { Input } from './ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { formatDueDate } from '../lib/dates';
import { cn } from '../lib/utils';

// Who works on one subtask: a small menu of assignable teammates. Kept open
// while toggling so several people can be picked in one go.
function AssignMenu({ subtask, members, onChange }) {
  const ids = (subtask.assignees || []).map((a) => a.id);
  const toggle = (id) => onChange(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="flex shrink-0 items-center gap-1 rounded px-1 py-0.5 text-[11px] text-muted-foreground transition hover:bg-accent hover:text-foreground"
          title={ids.length ? `assigned to ${subtask.assignees.map((a) => a.name).join(', ')} — click to change` : 'assign someone'}
        >
          {ids.length ? (
            <span className="flex -space-x-1">
              {subtask.assignees.slice(0, 3).map((a) => <UserAvatar key={a.id} user={a} className="h-4 w-4 rounded-sm ring-1 ring-card" />)}
            </span>
          ) : <><UserPlus className="h-3.5 w-3.5" /> assign</>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="text-xs text-muted-foreground">assign to</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {members.map((m) => (
          <DropdownMenuCheckboxItem
            key={m.id}
            checked={ids.includes(m.id)}
            onCheckedChange={() => toggle(m.id)}
            onSelect={(e) => e.preventDefault()}
            className="gap-2 text-sm"
          >
            <UserAvatar user={m} className="h-4 w-4 rounded-sm" /> {m.name}
          </DropdownMenuCheckboxItem>
        ))}
        {members.length === 0 ? <p className="px-2 py-1 text-xs text-muted-foreground">No assignable team members.</p> : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// The checklist of a ticket's subtasks: tick to complete, click to open,
// assign inline, add with Enter. Subtasks are real tickets, so everything
// here goes through the same save path as the board.
export default function SubtaskList({ parent }) {
  const { tickets, members, saveTicket, removeTicket, openEditTicket } = useTickets();
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const assignable = members.filter((m) => m.is_assignable);

  const subs = useMemo(() => (
    tickets.filter((t) => t.parent_id === parent.id).sort((a, b) => (a.position - b.position) || (a.id - b.id))
  ), [tickets, parent.id]);
  const done = subs.filter((s) => s.status === 'done').length;

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(err?.response?.data?.detail || Object.values(err?.response?.data || {}).flat().join(' ') || 'Could not save the subtask.');
    } finally {
      setBusy(false);
    }
  };

  // Enter adds. Not a <form>: this list also lives inside the ticket modal's
  // form, and a nested form would hand Enter to the modal (saving + closing it).
  const add = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    const t = title.trim();
    if (!t) return;
    await run(async () => {
      await saveTicket({
        title: t, status: 'todo', parent_id: parent.id,
        type: parent.type, priority: parent.priority, department: parent.department,
        requestor_id: parent.requestor?.id ?? null, assignee_ids: [],
      });
      setTitle('');
    });
  };

  const toggleDone = (sub) => run(() => saveTicket({ status: sub.status === 'done' ? 'todo' : 'done' }, sub.id));
  const assign = (sub, ids) => run(() => saveTicket({ assignee_ids: ids }, sub.id));
  const remove = (sub) => {
    if (!window.confirm(`Delete subtask “${sub.title}”?`)) return;
    run(() => removeTicket(sub.id));
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-mono text-sm text-muted-foreground">
          {'// subtasks'}{subs.length ? ` (${done}/${subs.length})` : ''}
        </h2>
        {subs.length ? (
          <div className="h-1.5 w-24 overflow-hidden rounded bg-muted" title={`${done} of ${subs.length} done`}>
            <div className={cn('h-full rounded', done === subs.length ? 'bg-emerald-500' : 'bg-primary')} style={{ width: `${(done / subs.length) * 100}%` }} />
          </div>
        ) : null}
      </div>
      {error ? <p className="mb-2 text-xs text-destructive">{error}</p> : null}
      {subs.length ? (
        <ul className="space-y-0.5">
          {subs.map((sub) => {
            const isDone = sub.status === 'done';
            return (
              <li key={sub.id} className="group flex items-center gap-2 rounded-md px-1 py-1 hover:bg-accent/40">
                <button
                  type="button"
                  onClick={() => toggleDone(sub)}
                  disabled={busy}
                  className={cn('shrink-0 rounded transition', isDone ? 'text-emerald-500' : 'text-muted-foreground hover:text-foreground')}
                  title={isDone ? 'Reopen' : 'Mark done'}
                  aria-label={isDone ? `reopen ${sub.title}` : `mark ${sub.title} done`}
                >
                  {isDone ? <SquareCheck className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => openEditTicket(sub)}
                  className={cn('min-w-0 flex-1 truncate text-left text-sm hover:underline', isDone && 'text-muted-foreground line-through')}
                  title={`open #${sub.id}`}
                >
                  <span className="mr-1 text-[11px] text-muted-foreground no-underline">#{sub.id}</span>{sub.title}
                </button>
                {sub.status !== 'done' && sub.status !== 'todo' ? (
                  <span className="shrink-0 rounded bg-muted px-1 text-[10px] text-muted-foreground">{sub.status.replace('_', ' ')}</span>
                ) : null}
                {sub.due_date ? (
                  <span className="shrink-0 text-[11px] text-muted-foreground" title={`due ${formatDueDate(sub.due_date)}`}>
                    {formatDueDate(sub.due_date, { short: true })}
                  </span>
                ) : null}
                <AssignMenu subtask={sub} members={assignable} onChange={(ids) => assign(sub, ids)} />
                <button
                  type="button"
                  onClick={() => remove(sub)}
                  disabled={busy}
                  className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                  title="Delete subtask"
                  aria-label={`delete ${sub.title}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">No subtasks yet — break the work down below and assign each piece.</p>
      )}
      <div className="mt-2">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={add}
          placeholder="Add a subtask and press Enter…"
          className="h-8 text-sm"
          disabled={busy}
          aria-label="New subtask title"
        />
      </div>
    </div>
  );
}
